import { fetchOpenAIText } from "../utils/openaiClient";

// Отдельная функция для генерации из слов
const generateFromWords = async (words, level) => {
  const prompt = `Create a matching halves exercise using these words/phrases. First create complete sentences using the words, then split each sentence into two parts.

WORDS/PHRASES: ${words}

LEVEL: ${level}

LEVEL REQUIREMENTS:
- A1: Simple present tense, basic vocabulary, short sentences (3-6 words)
- A2: Present/past tense, common vocabulary, medium sentences (5-8 words)
- B1: Present/past/future tense, intermediate vocabulary, longer sentences (6-12 words)
- B2: Complex tenses, advanced vocabulary, complex sentences (8-15 words)
- C1: Advanced grammar, sophisticated vocabulary, complex structures (10-20 words)
- C2: Native-level complexity, academic vocabulary, very complex structures (12-25 words)

INSTRUCTIONS:
1. Create 5-8 complete sentences using the provided words/phrases
2. Each sentence MUST be appropriate for ${level} level complexity
3. Use vocabulary and grammar structures that match the level
4. Then split each sentence into two logical parts
5. The left part should be a meaningful beginning
6. The right part should be a meaningful ending

EXAMPLES FOR ${level}:
Words: "bike, school, rain, sandwich"
A1: "I ride my bike" → left: "I ride", right: "my bike"
B1: "I usually ride my bike to school" → left: "I usually ride", right: "my bike to school"
C1: "Despite the weather forecast, I decided to ride my bike to school" → left: "Despite the weather forecast, I decided to ride", right: "my bike to school"

Return ONLY valid JSON:
{
  "title": "Matching Halves Exercise",
  "level": "${level}",
  "sentences": [
    {
      "id": "s1",
      "left": "Left part only",
      "right": "Right part only",
      "breakPoint": 0.5
    }
  ]
}`;

  return prompt;
};

// Отдельная функция для генерации из предложений
const generateFromSentences = async (sentences, level) => {
  // Разделяем предложения по точкам, восклицательным и вопросительным знакам
  const sentenceArray = sentences
    .split(/[.!?]+/)
    .map(s => s.trim())
    .filter(s => s.length > 0);

  const prompt = `You are a language teacher. I will give you ${sentenceArray.length} sentences. You must split EACH ONE into two parts.

SENTENCES TO SPLIT:
${sentenceArray.map((s, i) => `Sentence ${i + 1}: "${s}"`).join('\n')}

CRITICAL INSTRUCTIONS:
1. Take each sentence above
2. Split it into exactly 2 parts: left and right
3. The left + right parts must equal the original sentence
4. Do this for ALL ${sentenceArray.length} sentences
5. BOTH left and right parts must contain text - neither can be empty

EXAMPLES OF CORRECT SPLITTING:
"I do not ride a bike" → left: "I do not ride", right: "a bike"
"I like to go to school" → left: "I like to go", right: "to school"
"It rains" → left: "It", right: "rains"
"I eat a sandwich and an apple" → left: "I eat", right: "a sandwich and an apple"
"First, I wake up" → left: "First, I", right: "wake up"

WRONG EXAMPLES (DO NOT DO THIS):
"I do not ride a bike" → left: "I do not ride a bike", right: ""
"I like to go to school" → left: "I like to go to school", right: ""

MANDATORY:
- You must process ALL ${sentenceArray.length} sentences
- Each sentence must be split into 2 non-empty parts
- Do not skip any sentences

Return this exact JSON format with ${sentenceArray.length} entries:
{
  "title": "Matching Halves Exercise",
  "level": "${level}",
  "sentences": [
    {
      "id": "s1",
      "left": "Left part of sentence 1",
      "right": "Right part of sentence 1",
      "breakPoint": 0.5
    },
    {
      "id": "s2",
      "left": "Left part of sentence 2",
      "right": "Right part of sentence 2",
      "breakPoint": 0.5
    }${sentenceArray.length > 2 ? `,
    {
      "id": "s3",
      "left": "Left part of sentence 3",
      "right": "Right part of sentence 3",
      "breakPoint": 0.5
    }` : ''}${sentenceArray.length > 3 ? `,
    {
      "id": "s4",
      "left": "Left part of sentence 4",
      "right": "Right part of sentence 4",
      "breakPoint": 0.5
    }` : ''}${sentenceArray.length > 4 ? `,
    {
      "id": "s5",
      "left": "Left part of sentence 5",
      "right": "Right part of sentence 5",
      "breakPoint": 0.5
    }` : ''}${sentenceArray.length > 5 ? `,
    {
      "id": "s6",
      "left": "Left part of sentence 6",
      "right": "Right part of sentence 6",
      "breakPoint": 0.5
    }` : ''}${sentenceArray.length > 6 ? `,
    {
      "id": "s7",
      "left": "Left part of sentence 7",
      "right": "Right part of sentence 7",
      "breakPoint": 0.5
    }` : ''}${sentenceArray.length > 7 ? `,
    {
      "id": "s8",
      "left": "Left part of sentence 8",
      "right": "Right part of sentence 8",
      "breakPoint": 0.5
    }` : ''}
  ]
}`;

  return prompt;
};

export function useMatchingHalvesGeneratorLogic({
  matchingHalvesInput, setMatchingHalvesInput, matchingHalvesLevel,
  matchingHalvesMode, setMatchingHalvesMode,
  setMatchingHalvesLoading, generatedMatchingHalves, setGeneratedMatchingHalves,
  currentTab, activeEditor, onInsertToBoard,
  setShowMatchingHalvesGenerator, setSelectedTemplate,
}) {
  const handleMatchingHalvesGenerate = async () => {
    if (!matchingHalvesInput.trim()) return;

    setMatchingHalvesLoading(true);
    try {
      // Выбираем соответствующую функцию генерации
      const prompt = matchingHalvesMode === 'words'
        ? await generateFromWords(matchingHalvesInput, matchingHalvesLevel)
        : await generateFromSentences(matchingHalvesInput, matchingHalvesLevel);



      const systemPrompt = matchingHalvesMode === 'words'
        ? "You are a helpful English teacher assistant that creates sentences from words and splits them into halves."
        : "You are a helpful English teacher assistant that splits existing sentences into exactly TWO parts. Each part must contain text. Do NOT create new sentences. Do NOT leave any part empty.";

      const fullPrompt = `${systemPrompt}\n\n${prompt}`;

      const runOnce = async () => {
        const raw = await fetchOpenAIText({
          prompt: fullPrompt,
          temperature: 0.3,
          maxTokens: 800,
        });
        if (!raw) return null;
        let contentStr = raw.trim();
        if (contentStr.startsWith('```json')) {
          contentStr = contentStr.replace(/^```json[\r\n]+/, '').replace(/```$/, '').trim();
        } else if (contentStr.startsWith('```')) {
          contentStr = contentStr.replace(/^```[\r\n]+/, '').replace(/```$/, '').trim();
        }
        try {
          return JSON.parse(contentStr);
        } catch (_) {
          return null;
        }
      };

      // Retry once on a bad/unparsable response instead of silently
      // swapping in unrelated hardcoded fallback sentences (bike/rain/
      // sandwich) that had nothing to do with what the teacher typed.
      let jsonData = await runOnce();
      if (!jsonData) jsonData = await runOnce();
      if (!jsonData || !Array.isArray(jsonData.sentences) || jsonData.sentences.length === 0) {
        throw new Error('Could not generate the exercise — the AI response was invalid. Please try again.');
      }

      // Дополнительная проверка и исправление для режима предложений
      if (matchingHalvesMode === 'sentences' && jsonData.sentences) {
        const expectedCount = matchingHalvesInput.split(/[.!?]+/).filter(s => s.trim().length > 0).length;
        const generatedCount = jsonData.sentences.length;

        // Исправляем пустые части в существующих предложениях
        jsonData.sentences.forEach((sentence, index) => {
          if (!sentence.left || !sentence.right || sentence.left.trim() === '' || sentence.right.trim() === '') {
            // Получаем оригинальное предложение
            const sentenceArray = matchingHalvesInput.split(/[.!?]+/).map(s => s.trim()).filter(s => s.length > 0);
            const originalSentence = sentenceArray[index] || '';

            if (originalSentence) {
              const words = originalSentence.split(' ');
              let left, right;

              if (words.length <= 2) {
                left = words[0] || '';
                right = words.slice(1).join(' ') || '';
              } else if (words.length <= 4) {
                const midPoint = Math.ceil(words.length / 2);
                left = words.slice(0, midPoint).join(' ');
                right = words.slice(midPoint).join(' ');
              } else {
                const verbIndex = words.findIndex(word =>
                  ['is', 'are', 'was', 'were', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'can', 'could', 'should', 'must'].includes(word.toLowerCase())
                );

                if (verbIndex > 0 && verbIndex < words.length - 1) {
                  left = words.slice(0, verbIndex + 1).join(' ');
                  right = words.slice(verbIndex + 1).join(' ');
                } else {
                  const midPoint = Math.ceil(words.length / 2);
                  left = words.slice(0, midPoint).join(' ');
                  right = words.slice(midPoint).join(' ');
                }
              }

              // Убеждаемся, что обе части не пустые
              if (!left || !right || left.trim() === '' || right.trim() === '') {
                const midPoint = Math.ceil(words.length / 2);
                left = words.slice(0, midPoint).join(' ') || originalSentence;
                right = words.slice(midPoint).join(' ') || '';
              }

              sentence.left = left;
              sentence.right = right;
            }
          }
        });

        if (expectedCount !== generatedCount) {
          // Попробуем исправить, добавив недостающие предложения
          const sentenceArray = matchingHalvesInput.split(/[.!?]+/).map(s => s.trim()).filter(s => s.length > 0);
          const missingSentences = sentenceArray.slice(generatedCount);

          if (missingSentences.length > 0) {
            missingSentences.forEach((sentence, index) => {
              // Улучшенное разделение предложений
              const words = sentence.split(' ');
              let left, right;

              if (words.length <= 2) {
                // Для коротких предложений
                left = words[0] || '';
                right = words.slice(1).join(' ') || '';
              } else if (words.length <= 4) {
                // Для средних предложений - разделяем пополам
                const midPoint = Math.ceil(words.length / 2);
                left = words.slice(0, midPoint).join(' ');
                right = words.slice(midPoint).join(' ');
              } else {
                // Для длинных предложений - ищем глагол или союз
                const verbIndex = words.findIndex(word =>
                  ['is', 'are', 'was', 'were', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'can', 'could', 'should', 'must'].includes(word.toLowerCase())
                );

                if (verbIndex > 0 && verbIndex < words.length - 1) {
                  left = words.slice(0, verbIndex + 1).join(' ');
                  right = words.slice(verbIndex + 1).join(' ');
                } else {
                  // Fallback - разделяем пополам
                  const midPoint = Math.ceil(words.length / 2);
                  left = words.slice(0, midPoint).join(' ');
                  right = words.slice(midPoint).join(' ');
                }
              }

              // Убеждаемся, что обе части не пустые
              if (!left || !right) {
                const midPoint = Math.ceil(words.length / 2);
                left = words.slice(0, midPoint).join(' ') || sentence;
                right = words.slice(midPoint).join(' ') || '';
              }

              jsonData.sentences.push({
                id: `s${generatedCount + index + 1}`,
                left: left,
                right: right,
                breakPoint: 0.5
              });
            });
          }
        }
      }

      setGeneratedMatchingHalves(jsonData);
    } catch (e) {
      alert(e?.message || 'Could not generate the exercise. Please try again.');
    } finally {
      setMatchingHalvesLoading(false);
    }
  };

  const handleMatchingHalvesInsert = () => {
    if (!generatedMatchingHalves) return;

    if (currentTab === 0) {
      if (!activeEditor) return;
      const matchingHalvesContent = {
        type: 'matchingHalves',
        attrs: {
          sentences: generatedMatchingHalves.sentences,
          title: generatedMatchingHalves.title,
          level: generatedMatchingHalves.level
        }
      };
      activeEditor.chain().focus().insertContent(matchingHalvesContent).run();
    } else {
      onInsertToBoard(generatedMatchingHalves, 'matchinghalves');
    }

    setShowMatchingHalvesGenerator(false);
    setMatchingHalvesInput("");
    setGeneratedMatchingHalves(null);
    setSelectedTemplate(null);
  };

  const handleMatchingHalvesCancel = () => {
    setShowMatchingHalvesGenerator(false);
    setMatchingHalvesInput("");
    setMatchingHalvesMode('sentences');
    setGeneratedMatchingHalves(null);
    setSelectedTemplate(null);
  };

  return { handleMatchingHalvesGenerate, handleMatchingHalvesInsert, handleMatchingHalvesCancel };
}
